<template>
  <div class="app-shell">
    <header class="app-header">
      <img
        class="app-logo"
        src="@/assets/logoicone.svg"
        alt="Super Pagamentos"
      >
    </header>
    <main class="app-main">
      <router-view />
    </main>
  </div>
</template>

<style>
:root {
  --brand-blue: #0641fc;
  --brand-blue-hover: #0535d4;
  --whatsapp-green: #25d366;
  --whatsapp-green-hover: #1ebe57;
  --text: #1a1a1a;
  --text-muted: #5c6370;
  --border: #e6e8ec;
  --border-hover: #c5cad3;
  --bg: #ffffff;
  --bg-soft: #f5f7fa;
  --danger: #d93025;
  --radius: 12px;

  /* Escala mobile-first (fluida) */
  --space-xs: 0.5rem;
  --space-sm: 0.75rem;
  --space-md: 1rem;
  --space-lg: 1.25rem;
  --space-xl: 1.75rem;
  --space-2xl: 2.25rem;

  --font-xs: clamp(0.75rem, 0.7rem + 0.25vw, 0.8125rem);
  --font-sm: clamp(0.8125rem, 0.75rem + 0.3vw, 0.875rem);
  --font-base: clamp(0.9375rem, 0.88rem + 0.35vw, 1rem);
  --font-md: clamp(1rem, 0.94rem + 0.4vw, 1.0625rem);
  --font-lg: clamp(1.0625rem, 0.98rem + 0.5vw, 1.2rem);
  --font-xl: clamp(1.125rem, 1.02rem + 0.65vw, 1.375rem);

  --tap: 48px;
  --page-x: max(1rem, env(safe-area-inset-left));
  --page-x-right: max(1rem, env(safe-area-inset-right));
  --page-bottom: max(1.25rem, env(safe-area-inset-bottom));
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

html {
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;
}

html,
body,
#app {
  margin: 0;
  min-height: 100%;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  font-size: var(--font-base);
  line-height: 1.45;
  background: var(--bg);
  color: var(--text);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

button,
input,
select {
  font: inherit;
  -webkit-tap-highlight-color: transparent;
}

button {
  touch-action: manipulation;
}

.app-shell {
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  background: var(--bg);
}

.app-header {
  display: flex;
  justify-content: center;
  flex-shrink: 0;
  padding: max(1.75rem, calc(env(safe-area-inset-top) + 1rem)) var(--page-x) 1rem;
  padding-right: var(--page-x-right);
}

.app-logo {
  width: clamp(120px, 38vw, 150px);
  height: auto;
  display: block;
}

.app-main {
  flex: 1;
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  padding: var(--space-md) var(--page-x) var(--page-bottom);
  padding-right: var(--page-x-right);
}

/* Aviso leve — não compete com a logo nem com as opções */
.notice {
  margin: 0 0 var(--space-lg);
  padding: 0.85rem 1rem;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(6, 65, 252, 0.04), rgba(6, 65, 252, 0.015));
  border: 1px solid rgba(6, 65, 252, 0.08);
  text-align: left;
}

.notice-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.45rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  background: rgba(6, 65, 252, 0.08);
  color: var(--brand-blue);
  font-size: var(--font-xs);
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1.2;
}

.notice-tag::before {
  content: '';
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--brand-blue);
  box-shadow: 0 0 0 3px rgba(6, 65, 252, 0.15);
}

.notice-text {
  margin: 0;
  font-size: var(--font-sm);
  font-weight: 400;
  line-height: 1.55;
  color: var(--text-muted);
  text-wrap: pretty;
}

.notice-text em {
  font-style: normal;
  color: var(--text);
  font-weight: 500;
}

.notice--center {
  text-align: center;
}

.notice--center .notice-tag {
  margin-left: auto;
  margin-right: auto;
}

.page-subtitle {
  margin: 0 0 var(--space-lg);
  font-size: var(--font-sm);
  font-weight: 400;
  line-height: 1.55;
  text-align: center;
  color: var(--text-muted);
  text-wrap: pretty;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: var(--tap);
  padding: 0.85rem 1.1rem;
  border: none;
  border-radius: var(--radius);
  font-size: var(--font-md);
  font-weight: 600;
  line-height: 1.25;
  cursor: pointer;
  transition: background-color 0.15s ease, opacity 0.15s ease;
  text-decoration: none;
  text-align: center;
}

.btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--brand-blue);
  color: #fff;
}

.btn-primary:not(:disabled):active {
  background: var(--brand-blue-hover);
}

.btn-whatsapp {
  background: var(--whatsapp-green);
  color: #fff;
}

.btn-whatsapp:not(:disabled):active {
  background: var(--whatsapp-green-hover);
}

/* CTA abaixo do conteúdo */
.page-actions {
  margin-top: var(--space-lg);
  padding-bottom: max(0.5rem, env(safe-area-inset-bottom));
}

.error-message {
  margin: var(--space-sm) 0 0;
  color: var(--danger);
  font-size: var(--font-sm);
  line-height: 1.4;
  text-align: center;
}

@media (min-width: 480px) {
  :root {
    --page-x: 1.25rem;
    --page-x-right: 1.25rem;
  }

  .app-main {
    padding-top: var(--space-lg);
  }
}

@media (hover: hover) {
  .btn-primary:not(:disabled):hover {
    background: var(--brand-blue-hover);
  }

  .btn-whatsapp:not(:disabled):hover {
    background: var(--whatsapp-green-hover);
  }
}
</style>
